import { useCallback, useMemo, useState } from 'react';

import IconNewNavRight from '../../../assets/svg/IconNewNavRight';
import { useOrientation } from '../../../boot/OrientationContext';
import { AnalyticsEvent } from '../../../constants/AnalyticsEvent';
import { warrior } from '../../../constants/color';
import ElementType from '../../../constants/ElementType';
import { MktType } from '../../../constants/MktType';
import { translate } from '../../../i18n';
import { LogEventPayload } from '../../../redux/actions/analyticsLog';
import { DeviceType } from '../../../types/Common';
import { getDeviceType } from '../../../utils/layoutHelpers';
import pageNavigation from '../../../utils/pageNavigation';
import LoadingItem from './LoadingItem';
import S, { getImageSize, getItemWidth } from './styles';
import TitleSection from './TitleSection';

interface Props {
  id: string;
  showLoading: boolean;
  title: string;
  images: string[];
  url: string;
  index: number;
  height: number;
  badgeIndex: number;
  logEvent: (payload: LogEventPayload) => void;
}

export const layoutName = 'index_operation';

export default ({
  id,
  images,
  index,
  height,
  url,
  title,
  showLoading,
  badgeIndex,
  logEvent,
}: Props) => {
  const isLandscape = useOrientation();

  const width = getItemWidth();
  const imageSize = getImageSize(width);

  const shouldShowBadge = badgeIndex === index;

  const [responsiveSize, setResponsiveSize] = useState(imageSize);
  const isLarge = useMemo(() => {
    const isMobile = getDeviceType() === DeviceType.MOBILE;

    return !isMobile || isLandscape;
  }, [isLandscape]);

  const imagesToShow = useMemo(() => {
    if (isLarge) {
      return images;
    }

    return images.slice(0, 2);
  }, [images, isLarge]);

  const handleLayout = useCallback(
    e => {
      const size = isLarge ? e.nativeEvent.layout.height : imageSize;

      setResponsiveSize(size);
    },
    [isLarge, imageSize, setResponsiveSize]
  );

  const handleOnPress = useCallback(() => {
    pageNavigation(url, {
      title,
    });

    logEvent({
      logEvent: AnalyticsEvent.Click,
      data: {
        elementType: ElementType.PromoteItem,
        mktType: MktType.Image,
        pitNumber: index + 1,
        id,
        layoutName,
      },
    });
    logEvent({
      logEvent: AnalyticsEvent.HomePageClick,
      data: {
        layoutName,
        title,
        index: index + 1,
      },
    });
  }, [url, title, index, id, logEvent]);

  return (
    <S.Container height={height}>
      <S.Touchable
        testID="container"
        accessibilityRole="link"
        href={url}
        onPress={handleOnPress}
        disabled={showLoading}
      >
        {showLoading ? (
          <LoadingItem isLarge={isLarge} width={width} imageSize={imageSize} />
        ) : (
          <>
            <TitleSection title={title} showBadge={shouldShowBadge} />
            <S.SubtitleContainer>
              <S.Subtitle>{translate('shopping-now')}</S.Subtitle>
              <IconNewNavRight size={8} fillColor={warrior} />
            </S.SubtitleContainer>
            <S.ImagesSection isLarge={isLarge} onLayout={handleLayout}>
              {imagesToShow.map(uri => {
                return (
                  <S.Image
                    key={uri}
                    size={responsiveSize}
                    source={{ uri }}
                    isLarge={isLarge}
                  />
                );
              })}
            </S.ImagesSection>
          </>
        )}
      </S.Touchable>
    </S.Container>
  );
};
